import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import ScriptLoader from 'react-script-loader-hoc';
import withGlobals from '../../components/Globals';
import Dropdown from '../../components/Dropdown';
import Form from '../../components/Form';
import Input from '../../components/Input';
import Label from '../../components/Label';
import Button from '../../components/Button';
import Dimmer from '../../components/Dimmer';
import Header from '../../components/Header';
import Modal from '../../components/Modal';
import Grid from '../../components/Grid';
import Image from '../../components/Image';
import Icon from '../../components/Icon';

import successImg from './success.svg';
import messages from './messages';
import jobLevelOptions from './jobLevelOptions';
import jobTopicOptions from './jobTopicOptions';
import { validateFields, elementIds } from '../../utils/upgradeForm';
import { loadMarketoForm, renderMarketoForm, submitMarketoForm } from '../../utils/marketoForm';
import { MARKETO_SCRIPT_URL, MARKETO_UPGRADE_FORM } from '../UpgradeForm/constants';
import { ADD_JOB_TOPIC_FIELD, MODIFY_MESSAGE_FIELD } from '../Authorization/constants';

import {
  ELOQUA_URL,
  PRIVACY_URL,
  ELOQUA_SUBSCRIPTION_CAMPAIGN_IDS,
  MARKETO_CAMPAIGN_ID,
  MARKETO_LEAD_SOURCE,
} from './constants';

import './styles.css';

const marketoUrl = MARKETO_SCRIPT_URL;
const { origin } = new URL(marketoUrl);
global.__webpack_marketo_public_path__ = origin;

const eloquaCampaignIds = ELOQUA_SUBSCRIPTION_CAMPAIGN_IDS;

function resolveCampaignId(subscription, useMarketo) {
  /* istanbul ignore next */
  return (useMarketo ? MARKETO_CAMPAIGN_ID : eloquaCampaignIds[subscription]) || '';
}

class SubscriptionUpgradeForm extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object,
    open: PropTypes.bool,
    subscription: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    marketoUtils: PropTypes.shape({
      loadMarketoForm: PropTypes.func.isRequired,
      submitMarketoForm: PropTypes.func.isRequired,
      renderMarketoForm: PropTypes.func.isRequired,
    }),
    scriptsLoadedSuccessfully: PropTypes.bool,
    name: PropTypes.string,
  };

  static defaultProps = {
    open: false,
  };

  constructor(props) {
    super(props);
    this.state = this.getInitialState();
    if (this.props.open) {
      this.trackModalOpen();
    }
    this.addJobTopicField = this.props.viewer?.enabledFeatures?.includes(ADD_JOB_TOPIC_FIELD);
    this.modifyMessageField = this.props.viewer?.enabledFeatures?.includes(MODIFY_MESSAGE_FIELD);
  }

  getInitialState = () => {
    const { viewer } = this.props;
    return {
      firstName: viewer.firstName,
      lastName: viewer.lastName,
      emailAddress: viewer.email,
      phone: viewer.phone,
      notes: viewer.notes || '',
      optIn: false,
      errors: {},
      success: false,
      currentCount: 10,
      timer: null,
      jobLevel: viewer.jobLevel || '',
      jobTopic: viewer.jobTopic || '',
      jobLevelError: false,
      jobTopicError: false,
      useMarketoUpgradeForm: viewer.enabledFeatures?.includes(MARKETO_UPGRADE_FORM),
      scriptsLoadedSuccessfully: false,
    };
  };

  componentWillReceiveProps(nextProps) {
    const { open, marketoUtils } = this.props;
    if (!open && nextProps.open) {
      this.trackModalOpen();
    }
    if (!this.state.scriptsLoadedSuccessfully &&
        nextProps.scriptsLoadedSuccessfully &&
        nextProps.viewer.enabledFeatures?.includes(MARKETO_UPGRADE_FORM)) {
      marketoUtils ? marketoUtils.loadMarketoForm(this) : loadMarketoForm(this); // eslint-disable-line no-unused-expressions
    }
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    if (e.target.type === 'checkbox') {
      value = !this.state[name]; // e.target.checked does not work in unit tests
    }
    this.setState({ [name]: value });
  };

  onSuccess = () => {
    /** Emitting GA event */
    window.dataLayer.push({
      event: 'trackEvent',
      eventCategory: 'User Engagement',
      eventAction: 'eloqua-lead-gen-submitted',
      userId: this.props.viewer.uuid,
    });

    this.setState({
      timer: setInterval(this.countDown, 1000),
      success: true,
    });
  };

  trackModalOpen() {
    /** Emitting GA event */
    window.dataLayer.push({
      event: 'trackEvent',
      eventCategory: 'User Engagement',
      eventAction: 'eloqua-lead-gen-load',
      userId: this.props.viewer.uuid,
    });
  }

  stopTimer = () => {
    clearInterval(this.state.timer);
  };

  countDown = () => {
    if (this.state.currentCount === 1) {
      this._close();
    } else {
      this.setState((prevState) => ({
        currentCount: prevState.currentCount - 1,
      }));
    }
  };

  _close = () => {
    const { onClose } = this.props;
    this.stopTimer();
    this.setState(this.getInitialState());
    onClose();
  };

  _validateForm = (e) => {
    const { jobLevel, jobTopic, useMarketoUpgradeForm } = this.state;
    if (!jobLevel) {
      this.setState({ jobLevelError: true });
    }
    if (this.addJobTopicField && !jobTopic) {
      this.setState({ jobTopicError: true });
    }

    // eslint-disable-next-line react/no-access-state-in-setstate
    const errors = validateFields(this.state, this.addJobTopicField, this.modifyMessageField);
    this.setState({ errors });
    if (Object.keys(errors).some((key) => errors[key])) {
      e.preventDefault();
    } else {
      if (useMarketoUpgradeForm) { /* eslint-disable-line no-lonely-if */
        const { viewer, subscription, name, marketoUtils } = this.props;
        /* istanbul ignore next */
        const campaignId = resolveCampaignId(subscription || name, true);
        const leadSource = MARKETO_LEAD_SOURCE[subscription || name] || 'Unspecified';
        /* eslint-disable no-unused-expressions */
        marketoUtils ?
          marketoUtils.submitMarketoForm(viewer, this.state, campaignId, leadSource, this.onSuccess, this.addJobTopicField) :
          submitMarketoForm(viewer, this.state, campaignId, leadSource, this.onSuccess, this.addJobTopicField);
        /* eslint-enable no-unused-expressions */
      } else {
        this.onSuccess();
      }
    }
  };

  onChangeMenuDropdown = (key, value) => {
    this.setState({ [`${key}Error`]: false, [key]: value });
  }

  render() {
    const { viewer, open, intl, subscription, marketoUtils } = this.props;
    const {
      firstName,
      lastName,
      emailAddress,
      phone,
      errors,
      success,
      currentCount,
      notes,
      optIn,
      jobLevelError,
      jobTopicError,
      jobLevel,
      jobTopic,
      useMarketoUpgradeForm,
    } = this.state;
    const header = intl.formatMessage(messages.header);
    const successAlt = intl.formatMessage(messages.successAlt);
    const campaignId = resolveCampaignId(subscription, false);

    return (
      <Dimmer.Dimmable
        role="dialog"
        aria-label={header}
        as={Modal}
        open={open}
        id="upgrade-form"
        onClose={this._close}
        closeIcon={<Icon name="close" styleName="upgrade-form-close" />}
        className="upgrade-form"
      >
        <Header styleName="header">{header}</Header>
        <Modal.Content styleName="no-padding">
          <Grid
            as={Form}
            onSubmit={this._validateForm}
            method="post"
            action={useMarketoUpgradeForm ? undefined : ELOQUA_URL}
            target="bgsubmit"
            autoComplete="off"
            styleName="no-margin"
            stackable
          >
            <Grid.Row styleName="form-container">
              <Grid.Column verticalAlign="middle" styleName={(success ? 'hidden-form' : 'full-height padding-bottom')}>
                {!useMarketoUpgradeForm && (
                  <React.Fragment>
                    <input type="hidden" name="elqFormName" value="standardGlobal" />
                    <input type="hidden" name="elqSiteId" value="2203" />
                    <input type="hidden" id="field0" name="redirectURL" value="" />
                    <input type="hidden" id="field5" name="offerCampaignId" value={campaignId} />
                    <input type="hidden" id="field11" name="Market" value="Matching" />
                    <input type="hidden" id="field19" name="leadSource" value="Information Request" />
                    <input type="hidden" id="field21" name="country" value={viewer.institution.field_country || 'United States'} />
                    <input type="hidden" id="field22" name="jobLevel" value={jobLevel} />
                    {this.addJobTopicField && <input type="hidden" id="topic" name="jobTopic" value={jobTopic} />}
                    <input type="hidden" id="field23" name="institutionState" value={get(viewer, 'institution.field_state_short_name')} />
                  </React.Fragment>
                )}
                <Header as="h3">
                  <FormattedMessage {...messages.contactInformation} />
                </Header>
                <div styleName="notice">
                  <FormattedMessage {...messages.upgradeNotice} />
                </div>
                <div styleName="required-notice">Required Fields</div>
                <Form.Field error={errors.firstName}>
                  <label styleName="form-label required-label" htmlFor={elementIds.firstName}>
                    <FormattedMessage {...messages.firstName} />
                  </label>
                  <Input type="text" name="firstName" value={firstName || ''} onChange={this.onChange} fluid>
                    <input id={elementIds.firstName} aria-invalid={errors.firstName} aria-required="true" />
                  </Input>
                  {errors.firstName && (
                  <Label role="tooltip" basic color="red" pointing data-cy="first-name-validation-error">
                    <FormattedMessage {...messages.enterValue} />
                  </Label>
                  )}
                </Form.Field>
                <Form.Field error={errors.lastName}>
                  <label styleName="form-label required-label" htmlFor={elementIds.lastName}>
                    <FormattedMessage {...messages.lastName} />
                  </label>
                  <Input type="text" name="lastName" value={lastName || ''} onChange={this.onChange} fluid>
                    <input id={elementIds.lastName} aria-invalid={errors.lastName} aria-required="true" />
                  </Input>
                  {errors.lastName && (
                  <Label role="tooltip" basic color="red" pointing data-cy="last-name-validation-error">
                    <FormattedMessage {...messages.enterValue} />
                  </Label>
                  )}
                </Form.Field>
                <Form.Field error={errors.emailAddress || errors.invalidEmail}>
                  <label styleName="form-label required-label" htmlFor={elementIds.emailAddress}>
                    <FormattedMessage {...messages.email} />
                  </label>
                  <Input type="email" name="emailAddress" value={emailAddress || ''} onChange={this.onChange} fluid>
                    <input id={elementIds.emailAddress} aria-invalid={errors.emailAddress || errors.invalidEmail} aria-required="true" />
                  </Input>
                  {errors.emailAddress && (
                  <Label role="tooltip" basic color="red" pointing data-cy="email-validation-error">
                    <FormattedMessage {...messages.enterValue} />
                  </Label>
                  )}
                  {errors.invalidEmail && (
                  <Label role="tooltip" basic color="red" pointing>
                    <FormattedMessage {...messages.invalidEmail} />
                  </Label>
                  )}
                </Form.Field>
                <Form.Field error={errors.invalidPhone}>
                  <label styleName="form-label" htmlFor={elementIds.phone}>
                    <FormattedMessage {...messages.phone} />
                  </label>
                  <Input type="tel" name="phone" value={phone || ''} onChange={this.onChange} fluid>
                    <input id={elementIds.phone} aria-invalid={errors.invalidPhone} autoComplete="phone" />
                  </Input>
                  {errors.invalidPhone && (
                  <Label role="tooltip" basic color="red" pointing>
                    <FormattedMessage {...messages.invalidPhone} />
                  </Label>
                  )}
                </Form.Field>
                <Form.Field>
                  <label styleName="form-label required-label" htmlFor={elementIds.company}>
                    <FormattedMessage {...messages.company} />
                  </label>
                  <Input type="text" name="company" value={/* istanbul ignore next */ viewer.institution.name || ''} fluid>
                    <input id={elementIds.company} styleName="fake-disabled-input" aria-required="true" required readOnly />
                  </Input>
                </Form.Field>
                <Form.Field>
                  <label styleName="form-label required-label" htmlFor={elementIds.jobLevel}>
                    <FormattedMessage {...messages.jobLevel} />
                  </label>
                  <Dropdown
                    className="upgrade-form-dropdown"
                    id={elementIds.jobLevel}
                    name="jobLevel"
                    placeholder="Select Job Level"
                    fluid
                    selection
                    options={jobLevelOptions}
                    aria-required="true"
                    onChange={(ev, { value }) => { this.onChangeMenuDropdown('jobLevel', value); }}
                    error={jobLevelError}
                  />
                </Form.Field>
                {this.addJobTopicField && (
                  <Form.Field>
                    <label styleName="form-label required-label" htmlFor={elementIds.jobTopic}>
                      <FormattedMessage {...messages.jobTopic} />
                    </label>
                    <Dropdown
                      className="upgrade-form-dropdown"
                      id={elementIds.jobTopic}
                      name="jobTopic"
                      placeholder="Select type of request"
                      fluid
                      selection
                      options={jobTopicOptions}
                      aria-required="true"
                      onChange={(ev, { value }) => { this.onChangeMenuDropdown('jobTopic', value); }}
                      error={jobTopicError}
                    />
                  </Form.Field>
                )}
                {!this.modifyMessageField && (
                  <Form.Field>
                    <label styleName="form-label" htmlFor={elementIds.notes}>
                      Message (optional)
                    </label>
                    <textarea
                      rows="3"
                      maxLength="500"
                      id={elementIds.notes}
                      value={notes || ''}
                      onChange={this.onChange}
                      name="notes"
                    />
                  </Form.Field>
                )}
                {this.modifyMessageField && (
                  <Form.Field className="field-notes">
                    <label styleName="form-label required-label" htmlFor={elementIds.notes}>
                      <FormattedMessage {...messages.notes} />
                    </label>
                    <textarea
                      rows="3"
                      maxLength="500"
                      id={elementIds.notes}
                      value={notes || ''}
                      onChange={this.onChange}
                      name="notes"
                      aria-invalid={errors.notes}
                      aria-required="true"
                    />
                    {errors.notes && (
                      <Label role="tooltip" basic color="red" pointing data-cy="notes-validation-error">
                        <FormattedMessage {...messages.enterValue} />
                      </Label>
                    )}
                  </Form.Field>
                )}
                <Form.Field>
                  <div className="ui checkbox">
                    <input
                      type="checkbox"
                      checked={optIn}
                      onChange={this.onChange}
                      className="ui checkbox"
                      id={elementIds.optIn}
                      name="optIn"
                    />
                    <label styleName="form-label" htmlFor={elementIds.optIn}>
                      <FormattedMessage {...messages.hobsonsCommunication} />
                    </label>
                  </div>
                </Form.Field>
              </Grid.Column>
              {success && (
              <Grid.Column width={16} verticalAlign="middle" styleName="success-message">
                <Image src={successImg} alt={successAlt} centered />
                <b><FormattedMessage {...messages.thanksTitle} /></b>
                <p><FormattedMessage {...messages.thanksMessage} /></p>
              </Grid.Column>
              )}
            </Grid.Row>
            <Grid.Row styleName="no-padding">
              <Grid.Column styleName="footer">
                {!success && (
                <div>
                  <Button type="submit" color="pink">
                    <FormattedMessage {...messages.requestInformation} />
                  </Button>
                  <a styleName="privacy-link" href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Statement</a>
                </div>
                )}
                {success && <FormattedMessage {...messages.closingCount} values={{ count: currentCount }} />}
              </Grid.Column>
            </Grid.Row>
          </Grid>
          {useMarketoUpgradeForm && (marketoUtils ? marketoUtils.renderMarketoForm() : renderMarketoForm())}
          <iframe title="hidden" styleName="hidden" name="bgsubmit" id="bgsubmit" />
        </Modal.Content>
      </Dimmer.Dimmable>
    );
  }
}

export const SubscriptionUpgradeFormComponent = injectIntl(SubscriptionUpgradeForm);

export default withGlobals(ScriptLoader(marketoUrl)(SubscriptionUpgradeFormComponent));
